import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Typography, Paper, Box, Button, List, ListItem, ListItemText, ListItemAvatar,
    Avatar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tabs, Tab, TextField, FormControl, InputLabel, Select, MenuItem,
    Card, CardContent, Divider, Chip, ListItemIcon, Tooltip, Alert, Autocomplete, CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import CancelIcon from '@mui/icons-material/Cancel';
import FlagIcon from '@mui/icons-material/Flag';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import StoreIcon from '@mui/icons-material/Store';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';

import axios from 'axios';
import API_URL from '../../config';
import Loading from '../../components/Loading';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function SoftwareLicenseDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [license, setLicense] = useState(null);
    const [renewals, setRenewals] = useState([]);
    const [files, setFiles] = useState([]);
    const [logs, setLogs] = useState([]);
    const [users, setUsers] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [countries, setCountries] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);

    // Chat
    const [conversations, setConversations] = useState([]);
    const [newMessage, setNewMessage] = useState('');

    // Edit Mode
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState({});

    // Renewals
    const [openRenewal, setOpenRenewal] = useState(false);
    const [newRenewal, setNewRenewal] = useState({ renewal_date: '', start_date: '', end_date: '', cost: '', remarks: '' });

    // Drag and Drop
    const [dragging, setDragging] = useState(false);

    // AD Search State
    const [adUsers, setAdUsers] = useState([]);
    const [adLoading, setAdLoading] = useState(false);

    const handleADSearch = async (query) => {
        if (!query || query.length < 3) {
            setAdUsers([]);
            return;
        }
        setAdLoading(true);
        try {
            const res = await axios.get(`${API_URL}/tickets/search-ad-users?q=${query}`, { headers });
            setAdUsers(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setAdLoading(false);
        }
    };

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchLicense();
        fetchRenewals();
        fetchFiles();
        fetchLogs();
        fetchSetupData();
        fetchConversations();
    }, [id]);

    const fetchConversations = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses/${id}/conversations`, { headers });
            setConversations(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchLogs = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses/${id}/logs`, { headers });
            setLogs(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchSetupData = async () => {
        try {
            const [resUsers, resDivs, resCountries, resSuppliers] = await Promise.all([
                axios.get(`${API_URL}/setup/users`, { headers }),
                axios.get(`${API_URL}/setup/divisions`, { headers }),
                axios.get(`${API_URL}/setup/countries`, { headers }),
                axios.get(`${API_URL}/it-suppliers`, { headers })
            ]);
            setUsers(resUsers.data);
            setDivisions(resDivs.data);
            setCountries(resCountries.data);
            setSuppliers(resSuppliers.data);
        } catch (e) { console.error(e); }
    };

    const fetchLicense = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses/${id}`, { headers });
            setLicense(res.data);
            setEditData(res.data);
            setLoading(false);
        } catch (e) { console.error(e); setLoading(false); }
    };

    const fetchRenewals = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses/${id}/renewals`, { headers });
            setRenewals(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchFiles = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses/${id}/files`, { headers });
            setFiles(res.data);
        } catch (e) { console.error(e); }
    };

    const handleUpdate = async () => {
        try {
            await axios.put(`${API_URL}/software-licenses/${id}`, editData, { headers });
            await fetchLicense();
            await fetchLogs(); // Validation: Refresh logs after update
            setEditMode(false);
        } catch (e) { console.error(e); }
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this license?")) return;
        try {
            await axios.delete(`${API_URL}/software-licenses/${id}`, { headers });
            navigate('/software-licenses');
        } catch (e) { console.error(e); }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            const formData = new FormData();
            formData.append('file', file);
            try {
                await axios.post(`${API_URL}/software-licenses/${id}/files`, formData, { headers });
                fetchFiles();
            } catch (e) {
                console.error(e);
                alert("File upload failed: " + (e.response?.data?.error || e.message));
            }
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            await axios.post(`${API_URL}/software-licenses/${id}/files`, formData, { headers });
            fetchFiles();
        } catch (e) {
            console.error(e);
            alert("File upload failed: " + (e.response?.data?.error || e.message));
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;
        try {
            await axios.post(`${API_URL}/software-licenses/${id}/conversations`, { message: newMessage }, { headers });
            setNewMessage('');
            fetchConversations();
        } catch (e) { console.error(e); }
    };

    const handleDeleteFile = async (fileId) => {
        if (!window.confirm("Delete this file?")) return;
        try {
            await axios.delete(`${API_URL}/software-licenses/${id}/files/${fileId}`, { headers });
            fetchFiles();
        } catch (e) { console.error(e); }
    };

    const handleAddRenewal = async () => {
        try {
            await axios.post(`${API_URL}/software-licenses/${id}/renewals`, newRenewal, { headers });
            setOpenRenewal(false);
            setNewRenewal({ renewal_date: '', start_date: '', end_date: '', cost: '', remarks: '' });
            fetchRenewals();
        } catch (e) { console.error(e); }
    };

    if (loading) return <Loading />;
    if (!license) return <Typography>License not found</Typography>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header Section */}
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, mb: 4, borderRadius: 4, background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', color: 'white' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ width: '100%' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/software-licenses')}
                            sx={{ color: 'white', mb: 2, textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                        >
                            Back to Software Licenses
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Typography variant="h3" fontWeight="bold">{license.name}</Typography>
                            <Chip label={license.status} sx={{ bgcolor: 'white', color: 'success.main', fontWeight: 'bold' }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', px: 2, py: 1, borderRadius: 2 }}>
                                <StoreIcon fontSize="small" />
                                <Typography>{license.supplier_name || 'No Supplier'}</Typography>
                            </Box>
                            {license.department_name && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', px: 2, py: 1, borderRadius: 2 }}>
                                    <BusinessIcon fontSize="small" />
                                    <Typography>{license.department_name}</Typography>
                                </Box>
                            )}
                            {license.country_name && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', px: 2, py: 1, borderRadius: 2 }}>
                                    <FlagIcon fontSize="small" />
                                    <Typography>{license.country_name}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {!editMode ? (
                            <>
                                <Button
                                    onClick={() => setEditMode(true)}
                                    variant="contained"
                                    size="medium"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                        height: 48,
                                        minWidth: 48,
                                        px: 0,
                                        borderRadius: 3
                                    }}
                                >
                                    <EditIcon />
                                </Button>
                                <Button
                                    onClick={handleDelete}
                                    variant="contained"
                                    size="medium"
                                    sx={{
                                        bgcolor: 'rgba(255,0,0,0.2)',
                                        color: 'white',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        '&:hover': { bgcolor: 'rgba(255,0,0,0.3)' },
                                        height: 48,
                                        minWidth: 48,
                                        px: 0,
                                        borderRadius: 3,
                                        ml: 1
                                    }}
                                >
                                    <DeleteIcon />
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    onClick={handleUpdate}
                                    variant="contained"
                                    size="medium"
                                    sx={{
                                        bgcolor: '#4caf50',
                                        color: 'white',
                                        height: 48,
                                        minWidth: 48,
                                        px: 0,
                                        borderRadius: 3,
                                        '&:hover': { bgcolor: '#388e3c' }
                                    }}
                                >
                                    <SaveIcon />
                                </Button>
                                <Button
                                    onClick={() => { setEditMode(false); setEditData(license); }}
                                    variant="contained"
                                    size="medium"
                                    sx={{
                                        bgcolor: '#f44336',
                                        color: 'white',
                                        height: 48,
                                        minWidth: 48,
                                        px: 0,
                                        borderRadius: 3,
                                        marginLeft: 1,
                                        '&:hover': { bgcolor: '#d32f2f' }
                                    }}
                                >
                                    <CancelIcon />
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Paper>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper sx={{ width: '100%', mb: 2, borderRadius: 3, overflow: 'hidden' }}>
                        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 2 }}>
                            <Tab label="Overview" icon={<DescriptionIcon />} iconPosition="start" />
                            <Tab label="Renewals" icon={<HistoryIcon />} iconPosition="start" />
                            <Tab label="Files" icon={<AttachFileIcon />} iconPosition="start" />
                            {/* <Tab label="History Log" icon={<HistoryIcon />} iconPosition="start" /> */}
                            <Tab label="Chat" icon={<ChatIcon />} iconPosition="start" />
                        </Tabs>

                        {/* Overview Tab */}
                        <TabPanel value={tab} index={0}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>License Details</Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    {editMode ? (
                                                        <TextField 
                                                            fullWidth label="App Name" 
                                                            value={editData.name} 
                                                            onChange={(e) => setEditData({...editData, name: e.target.value})} 
                                                        />
                                                    ) : (
                                                        <Box>
                                                            <Typography color="textSecondary" variant="caption">Application Name</Typography>
                                                            <Typography variant="body1" fontWeight="bold">{license.name}</Typography>
                                                        </Box>
                                                    )}
                                                </Grid>
                                                <Grid item xs={12}>
                                                    {editMode ? (
                                                        <TextField 
                                                            fullWidth label="Description" multiline rows={4}
                                                            value={editData.description} 
                                                            onChange={(e) => setEditData({...editData, description: e.target.value})} 
                                                        />
                                                    ) : (
                                                        <Box>
                                                            <Typography color="textSecondary" variant="caption">Description</Typography>
                                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{license.description || 'No description provided.'}</Typography>
                                                        </Box>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>Context</Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    {editMode ? (
                                                        <Autocomplete
                                                            options={suppliers}
                                                            getOptionLabel={(option) => option.name}
                                                            value={suppliers.find(s => s.id === editData.supplier_id) || null}
                                                            onChange={(e, newValue) => setEditData({...editData, supplier_id: newValue ? newValue.id : null})}
                                                            renderInput={(params) => <TextField {...params} label="Supplier" />}
                                                        />
                                                    ) : (
                                                        <Box mb={2}>
                                                            <Typography color="textSecondary" variant="caption">Supplier</Typography>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <StoreIcon color="primary" fontSize="small" />
                                                                <Typography>{license.supplier_name || 'N/A'}</Typography>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Grid>
                                                
                                                <Grid item xs={12}>
                                                    {editMode ? (
                                                        <Autocomplete
                                                            freeSolo
                                                            options={adUsers}
                                                            loading={adLoading}
                                                            getOptionLabel={(option) => {
                                                                if (typeof option === 'string') return option;
                                                                return option.displayName || option.name || '';
                                                            }}
                                                            onInputChange={(event, newInputValue) => {
                                                                handleADSearch(newInputValue);
                                                            }}
                                                            filterOptions={(x) => x}
                                                            value={editData.requested_by_name ? { displayName: editData.requested_by_name, mail: editData.requested_by_email } : null}
                                                            onChange={(e, newValue) => {
                                                                if (typeof newValue === 'string') {
                                                                     setEditData({ 
                                                                        ...editData, 
                                                                        requested_by_name: newValue,
                                                                        requested_by_email: '',
                                                                        requested_by_id: null
                                                                    });
                                                                } else if (newValue) {
                                                                    const name = newValue.displayName || newValue.username;
                                                                    const email = newValue.mail || newValue.email || newValue.userPrincipalName;
                                                                    
                                                                    setEditData({ 
                                                                        ...editData, 
                                                                        requested_by_name: name,
                                                                        requested_by_email: email,
                                                                        requested_by_id: null
                                                                    });
                                                                } else {
                                                                    setEditData({ ...editData, requested_by_name: '', requested_by_email: '', requested_by_id: null });
                                                                }
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField 
                                                                    {...params} 
                                                                    label="Requested By" 
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        endAdornment: (
                                                                            <React.Fragment>
                                                                                {adLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                                {params.InputProps.endAdornment}
                                                                            </React.Fragment>
                                                                        ),
                                                                    }}
                                                                />
                                                            )}
                                                            renderOption={(props, option) => (
                                                                <li {...props}>
                                                                    <Box>
                                                                        <Typography variant="body1">{option.displayName || option.name}</Typography>
                                                                        {(option.mail || option.userPrincipalName) && <Typography variant="caption" color="textSecondary">{option.mail || option.userPrincipalName}</Typography>}
                                                                    </Box>
                                                                </li>
                                                            )}
                                                        />
                                                    ) : (
                                                        <Box mb={2}>
                                                            <Typography color="textSecondary" variant="caption">Requested By</Typography>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <PersonIcon color="primary" fontSize="small" />
                                                                <Box>
                                                                    <Typography>{license.requested_by_name || 'N/A'}</Typography>
                                                                    {license.requested_by_email && <Typography variant="caption" color="textSecondary" display="block">{license.requested_by_email}</Typography>}
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Grid>

                                                <Grid item xs={12}>
                                                    {editMode ? (
                                                        <Autocomplete
                                                            options={divisions}
                                                            getOptionLabel={(option) => option.name}
                                                            value={divisions.find(d => d.id === editData.department_id) || null}
                                                            onChange={(e, newValue) => setEditData({...editData, department_id: newValue ? newValue.id : null})}
                                                            renderInput={(params) => <TextField {...params} label="Department" />}
                                                        />
                                                    ) : (
                                                        <Box mb={2}>
                                                            <Typography color="textSecondary" variant="caption">Department</Typography>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <BusinessIcon color="primary" fontSize="small" />
                                                                <Typography>{license.department_name || 'N/A'}</Typography>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Grid>

                                                <Grid item xs={12}>
                                                    {editMode ? (
                                                        <Autocomplete
                                                            options={countries}
                                                            getOptionLabel={(option) => option.name}
                                                            value={countries.find(c => c.id === editData.country_id) || null}
                                                            onChange={(e, newValue) => setEditData({...editData, country_id: newValue ? newValue.id : null})}
                                                            renderInput={(params) => <TextField {...params} label="Country" />}
                                                        />
                                                    ) : (
                                                        <Box mb={2}>
                                                            <Typography color="textSecondary" variant="caption">Country</Typography>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <FlagIcon color="primary" fontSize="small" />
                                                                <Typography>{license.country_name || 'N/A'}</Typography>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Grid>

                                                <Grid item xs={12}>
                                                    {editMode ? (
                                                        <FormControl fullWidth>
                                                            <InputLabel>Status</InputLabel>
                                                            <Select
                                                                value={editData.status}
                                                                label="Status"
                                                                onChange={(e) => setEditData({...editData, status: e.target.value})}
                                                            >
                                                                <MenuItem value="Active">Active</MenuItem>
                                                                <MenuItem value="Expired">Expired</MenuItem>
                                                                <MenuItem value="Pending Renewal">Pending Renewal</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <Box>
                                                            <Typography color="textSecondary" variant="caption">Current Status</Typography>
                                                            <Chip label={license.status} size="small" color={license.status === 'Active' ? 'success' : 'default'} />
                                                        </Box>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* Renewals Tab */}
                        <TabPanel value={tab} index={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenRenewal(true)}>Log Renewal</Button>
                            </Box>
                            <TableContainer component={Paper} elevation={0} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Renewal Date</TableCell>
                                            <TableCell>Supplier</TableCell>
                                            <TableCell>Start Date</TableCell>
                                            <TableCell>End Date</TableCell>
                                            <TableCell>Cost</TableCell>
                                            <TableCell>Notes</TableCell>
                                            <TableCell>Created At</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {renewals.map(r => (
                                            <TableRow key={r.id}>
                                                <TableCell>{r.renewal_date}</TableCell>
                                                <TableCell>{r.supplier_name || '-'}</TableCell>
                                                <TableCell>{r.start_date || '-'}</TableCell>
                                                <TableCell>{r.end_date || '-'}</TableCell>
                                                <TableCell>{r.cost}</TableCell>
                                                <TableCell>{r.remarks}</TableCell>
                                                <TableCell>{new Date(r.logged_at).toLocaleDateString()}</TableCell>
                                            </TableRow>
                                        ))}
                                        {renewals.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">No renewal history.</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        {/* Files Tab */}
                        <TabPanel value={tab} index={2}>
                            <Box
                                onDragEnter={handleDragEnter}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                sx={{
                                    border: dragging ? '2px dashed #1976d2' : '2px dashed transparent',
                                    borderRadius: 2,
                                    p: 2,
                                    bgcolor: dragging ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                    transition: 'all 0.2s ease-in-out',
                                    position: 'relative',
                                    minHeight: 300
                                }}
                            >
                                {dragging && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'rgba(255, 255, 255, 0.8)',
                                            zIndex: 10,
                                            borderRadius: 2
                                        }}
                                    >
                                        <Typography variant="h5" color="primary">Drop files here to upload</Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                    <Button
                                        variant="contained"
                                        component="label"
                                        startIcon={<CloudUploadIcon />}
                                    >
                                        Upload File
                                        <input type="file" hidden onChange={handleFileUpload} />
                                    </Button>
                                </Box>
                                <List>
                                    {files.map(f => (
                                        <Paper key={f.id} variant="outlined" sx={{ mb: 1 }}>
                                            <ListItem
                                                secondaryAction={
                                                    <Box>
                                                        <IconButton href={`${API_URL}${f.file_path}`} target="_blank">
                                                            <DownloadIcon />
                                                        </IconButton>
                                                        <IconButton color="error" onClick={() => handleDeleteFile(f.id)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                }
                                            >
                                                <ListItemIcon>
                                                    <DescriptionIcon />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={f.filename}
                                                    secondary={new Date(f.upload_date).toLocaleString()}
                                                />
                                            </ListItem>
                                        </Paper>
                                    ))}
                                    {files.length === 0 && <Typography align="center" color="textSecondary">No files uploaded. Drag and drop to upload.</Typography>}
                                </List>
                            </Box>
                        </TabPanel>

                        {/* Logs Tab */}
                        <TabPanel value={tab} index={-1}>
                            <TableContainer component={Paper} elevation={0} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Timestamp</TableCell>
                                            <TableCell>User</TableCell>
                                            <TableCell>Action</TableCell>
                                            <TableCell>Details</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {logs.map(log => (
                                            <TableRow key={log.id}>
                                                <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                                                <TableCell>{log.user}</TableCell>
                                                <TableCell>{log.action}</TableCell>
                                                <TableCell>{log.details}</TableCell>
                                            </TableRow>
                                        ))}
                                        {logs.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">No history logs found.</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>

                        {/* Chat Tab */}
                        <TabPanel value={tab} index={3}>
                             <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Paper variant="outlined" sx={{ height: 500, display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                                        <Box flex={1} overflow="auto" p={2}>
                                            <List>
                                                {conversations.length === 0 && (
                                                    <Typography color="textSecondary" align="center" mt={4}>No conversations yet.</Typography>
                                                )}
                                                {conversations.map(c => (
                                                    <ListItem key={c.id} alignItems="flex-start">
                                                        <ListItemAvatar>
                                                            <Avatar sx={{ bgcolor: 'primary.main' }}>{c.user ? c.user[0] : 'U'}</Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText 
                                                            primary={
                                                                <Box display="flex" justifyContent="space-between">
                                                                    <Typography variant="subtitle2" fontWeight="bold">{c.user}</Typography>
                                                                    <Typography variant="caption" color="textSecondary">{new Date(c.timestamp).toLocaleString()}</Typography>
                                                                </Box>
                                                            } 
                                                            secondary={
                                                                <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                                                                    {c.message}
                                                                </Typography>
                                                            } 
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                        <Divider />
                                        <Box p={2} bgcolor="#F9FAFB">
                                            <Grid container spacing={2}>
                                                <Grid item xs>
                                                    <TextField 
                                                        fullWidth 
                                                        size="small" 
                                                        placeholder="Type a message..." 
                                                        value={newMessage} 
                                                        onChange={(e) => setNewMessage(e.target.value)} 
                                                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                                        InputProps={{ sx: { borderRadius: 2 } }}
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <Button variant="contained" endIcon={<SendIcon />} onClick={handleSendMessage} sx={{ borderRadius: 2 }}>
                                                        Send
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </TabPanel>
                    </Paper>
                </Grid>
            </Grid>

            {/* Log Renewal Dialog */}
            <Dialog open={openRenewal} onClose={() => setOpenRenewal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Log Renewal</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField 
                            label="Renewal Date" type="date" InputLabelProps={{ shrink: true }}
                            value={newRenewal.renewal_date} 
                            onChange={(e) => setNewRenewal({...newRenewal, renewal_date: e.target.value})} 
                        />
                        <Box display="flex" gap={2}>
                            <TextField 
                                fullWidth
                                label="Start Date" type="date" InputLabelProps={{ shrink: true }}
                                value={newRenewal.start_date} 
                                onChange={(e) => setNewRenewal({...newRenewal, start_date: e.target.value})} 
                            />
                             <TextField 
                                fullWidth
                                label="End Date" type="date" InputLabelProps={{ shrink: true }}
                                value={newRenewal.end_date} 
                                onChange={(e) => setNewRenewal({...newRenewal, end_date: e.target.value})} 
                            />
                        </Box>
                        <TextField 
                            label="Cost" 
                            value={newRenewal.cost} 
                            onChange={(e) => setNewRenewal({...newRenewal, cost: e.target.value})} 
                        />
                        <TextField 
                            label="Notes" multiline rows={3}
                            value={newRenewal.remarks} 
                            onChange={(e) => setNewRenewal({...newRenewal, remarks: e.target.value})} 
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenRenewal(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddRenewal}>Save Log</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default SoftwareLicenseDetails;
